import { cloneElement } from 'react';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import { Box, Indicator, NavItem } from '@optiaxiom/react';

import CanInstance from '@frontend/components/CanInstance';
import CanUser from '@frontend/components/CanUser';
import { FeatureFlag } from '@frontend/contexts';
import { InstanceOperationsName, OperationsName } from '@frontend/types';

import * as styles from './NavigationItem.css';

type NavigationItemProps = {
  path: string;
  icon: JSX.Element;
  label: string;
  permissions: OperationsName[];
  instanceCapabilities?: InstanceOperationsName[];
  featureFlag?: string;
  featureFlagVariable?: string;
  showIndicator?: boolean;
  badgeContent?: string;
};

/* TODO: Add doc string */
export const NavigationItem = ({
  path,
  icon,
  label,
  permissions,
  instanceCapabilities = [],
  featureFlag,
  featureFlagVariable,
  showIndicator = false,
  badgeContent,
}: NavigationItemProps) => {
  const location = useLocation();
  const { instanceId } = useParams();
  const selectedRoute = location.pathname.split('/')[3] || 'chat';

  const [pathBase, pathQuery] = path.split('?');
  const isActive = pathQuery
    ? selectedRoute === pathBase && location.search === `?${pathQuery}`
    : selectedRoute === path;

  const baseIcon = cloneElement(icon, { filled: selectedRoute === path });

  const iconWithBadge = badgeContent ? (
    <Indicator content={badgeContent} intent="danger" variant="strong">
      {baseIcon}
    </Indicator>
  ) : showIndicator ? (
    <Box className={styles.iconWithIndicator}>
      {baseIcon}
      <Box className={styles.unreadIndicator} />
    </Box>
  ) : (
    baseIcon
  );

  const navItem = (
    <NavLink
      to={`/instances/${instanceId}/${path}`}
      style={{ textDecoration: 'none', color: 'inherit' }}
    >
      <Box className={styles.navItemContainer}>
        <NavItem active={isActive} icon={iconWithBadge}>
          {label}
        </NavItem>
      </Box>
    </NavLink>
  );

  const withPermission = <CanUser operations={permissions}>{navItem}</CanUser>;

  const withCapabilities =
    instanceCapabilities.length > 0 ? (
      <CanInstance capabilities={instanceCapabilities}>{withPermission}</CanInstance>
    ) : (
      withPermission
    );

  if (featureFlag) {
    return (
      <FeatureFlag featureKey={featureFlag} variable={featureFlagVariable}>
        {withCapabilities}
      </FeatureFlag>
    );
  }

  return withCapabilities;
};
